import { useEffect } from 'react'
import { CheckCircle2, XCircle } from 'lucide-react'
import {
  useGetCompetitionApproval,
  useGetCompetitionRuleProposals,
  useGetDriverCoaching,
  useGetZoneGuide,
} from '../../hooks/backend/performance'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import { CoachingCard } from './CoachingCard'
import { ZoneGuide } from './ZoneGuide'
import {
  type ApprovalSummary,
  type Coaching,
  type RuleProposal,
  type ZoneGuideData,
  CHECK_LABELS,
  gateLabel,
  rand,
  shortDate,
} from '../../utils/competition'

/**
 * Approval support for one closed week. Everything, including whether it is
 * ready, is decided in driver_competition_approval_summary_v1. There is no
 * approve button: approving queues the performance bonus for payroll, and that
 * stays outside the Worker.
 */
export function ApprovalPanel({ weekStart }: { weekStart: string }) {
  const feed = useGetCompetitionApproval()
  const trigger = feed.trigger
  useEffect(() => {
    trigger({ week_start: weekStart }, { skipCache: true }).catch(() => {})
  }, [trigger, weekStart])
  const s = feed.data as ApprovalSummary | null | undefined

  return (
    <section className="rounded-lg border border-border p-4">
      <h2 className="text-sm font-semibold text-foreground">Approval</h2>
      {feed.error ? (
        <p className="mt-2 text-sm text-destructive">{String(feed.error)}</p>
      ) : !s ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Checking the week against the source data…
        </p>
      ) : s.state === 'not_computed' ? (
        <p className="mt-2 text-sm text-muted-foreground">This week has not been computed.</p>
      ) : (
        <div className="mt-2 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={s.status === 'approved' || s.status === 'paid' ? 'green' : s.ready_to_approve ? 'green' : 'amber'}>
              {s.status === 'approved' || s.status === 'paid' ? 'Approved' : s.ready_to_approve ? 'Ready for approval' : 'Not ready yet'}
            </Chip>
            <p className="text-sm text-foreground">{s.summary}</p>
          </div>
          <ul className="space-y-1">
            {s.checks.map((c) => (
              <li key={c.check} className="flex items-start gap-2 text-sm">
                {c.ok ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
                )}
                <span>
                  <span className="font-medium">{CHECK_LABELS[c.check] ?? c.check}.</span>{' '}
                  <span className="text-muted-foreground">{c.detail}</span>
                </span>
              </li>
            ))}
          </ul>
          {s.winners.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground">Winners</p>
              <ul className="mt-1 space-y-1 text-sm">
                {s.winners.map((w) => (
                  <li key={w.prize_rank}>
                    <span className="font-medium">{w.driver_name}</span>, {rand(w.prize_zar)}. <span className="text-muted-foreground">{w.why}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.one_gate_away.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground">One gate away</p>
              <ul className="mt-1 space-y-1 text-sm">
                {s.one_gate_away.map((o) => (
                  <li key={o.driver_name}>
                    <span className="font-medium">{o.driver_name}</span> {rand(o.total_earnings_zar)} over {o.shifts} shifts,{' '}
                    <span className="text-muted-foreground">
                      {o.kind === 'pending' ? 'waiting on data for ' : 'missed '}
                      {lowerFirst(gateLabel(o.gate))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Approving queues each prize as a performance bonus on the month-end payslip, so it is not a button on this page.
          </p>
        </div>
      )}
    </section>
  )
}

/**
 * Proposed rule changes with their evidence and a stored before-and-after.
 * Nothing here changes a rule. A rule that decides pay changes by a new dated
 * setting, and only on Luyanda's word.
 */
export function RuleReviewPanel() {
  const feed = useGetCompetitionRuleProposals()
  const trigger = feed.trigger
  useEffect(() => {
    trigger({}, { skipCache: true }).catch(() => {})
  }, [trigger])
  const proposals = (feed.data as RuleProposal[] | null | undefined) ?? []
  if (feed.error) return <p className="text-sm text-destructive">{String(feed.error)}</p>
  if (proposals.length === 0) return null

  return (
    <section className="space-y-4">
      {proposals.map((p) => (
        <div key={p.id} className="rounded-lg border border-border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Rule review: {p.title}</h2>
            <Chip tone={p.decision ? (p.decision === 'rejected' ? 'red' : 'green') : 'amber'}>
              {p.decision ? `${p.decision.replace('_', ' ')}${p.decided_by ? ` by ${p.decided_by}` : ''}` : 'Waiting for a decision'}
            </Chip>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Proposed by {p.proposed_by}.{' '}
            {p.decision
              ? `Decided${p.decided_at ? ` on ${shortDate(p.decided_at)}` : ''}.`
              : "Nothing changes until a decision is made; rule changes that affect pay are Luyanda's call."}
          </p>
          {p.decision_note && (
            <p className="mt-2 rounded-md border border-border bg-muted/50 p-2 text-sm text-foreground">{p.decision_note}</p>
          )}
          <ol className="mt-3 space-y-2">
            {p.rationale.map((r, i) => (
              <li key={i} className="text-sm">
                <p className="font-medium text-foreground">
                  {i + 1}. {r.change}
                </p>
                <p className="text-muted-foreground">{r.evidence}</p>
                {r.effect && <p className="text-xs text-muted-foreground">{r.effect}</p>}
              </li>
            ))}
          </ol>
          {p.simulation && (
            <div className="mt-3 overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Week of</th>
                    <th className="px-3 py-2 text-right">Drivers</th>
                    <th className="px-3 py-2 text-right">Qualify today</th>
                    <th className="px-3 py-2 text-right">Qualify if accepted</th>
                    <th className="px-3 py-2">Winners if accepted</th>
                    <th className="px-3 py-2">One gate away if accepted</th>
                  </tr>
                </thead>
                <tbody>
                  {p.simulation.weeks.map((w) => (
                    <tr key={w.week_start} className="border-t border-border align-top">
                      <td className="px-3 py-2">{shortDate(w.week_start)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{w.drivers}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{w.before.qualified}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{w.after.qualified}</td>
                      <td className="px-3 py-2 text-xs">
                        {w.after.winners.length === 0
                          ? 'Nobody'
                          : w.after.winners.map((x) => `${x.rank}. ${x.name} ${rand(x.total)} (${x.shifts} shifts)`).join('; ')}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {w.after.one_gate_away.length === 0
                          ? 'Nobody'
                          : w.after.one_gate_away.map((x) => `${x.name}, ${lowerFirst(gateLabel(x.gate))}`).join('; ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {p.simulated_at && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Every week judged on the rules in force for the last week shown. Worked out {shortDate(p.simulated_at)}.
            </p>
          )}
        </div>
      ))}
    </section>
  )
}

export function ZoneGuidePanel() {
  const feed = useGetZoneGuide()
  const trigger = feed.trigger
  useEffect(() => {
    trigger({}, {}).catch(() => {})
  }, [trigger])
  if (feed.error) return <p className="text-sm text-destructive">{String(feed.error)}</p>
  return <ZoneGuide guide={feed.data as ZoneGuideData | null | undefined} />
}

/** One driver's coaching, opened under his row on the competition page. */
export function CoachingLoader({ driverId }: { driverId: string }) {
  const feed = useGetDriverCoaching()
  const trigger = feed.trigger
  useEffect(() => {
    trigger({ driver_id: driverId }, { skipCache: true }).catch(() => {})
  }, [trigger, driverId])
  if (feed.error) return <p className="mt-2 text-xs text-destructive">{String(feed.error)}</p>
  if (!feed.data) {
    return (
      <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner className="h-3 w-3" /> Loading…
      </p>
    )
  }
  return (
    <div className="max-w-3xl">
      <CoachingCard coaching={feed.data as Coaching} audience="staff" />
    </div>
  )
}

// "Average below R1,000 a shift" reads mid-sentence as "average below R1,000 a
// shift", not "r1,000": only the first letter drops.
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1)
}
